import React, { useRef, useState, useEffect, useLayoutEffect } from "react";

const useWindowSize = () => {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    const updateSize = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);
  return size;
};

const Index = () => {
  const canvasRef = useRef(null);
  const zoomRef = useRef(null);

  const [screenWidth, screenHeight] = useWindowSize();

  const [startX, setStartX] = useState(-2);
  const [endX, setEndX] = useState(1);
  const [startY, setStartY] = useState(1);
  const [endY, setEndY] = useState(-1);
  const [isDragging, setIsDragging] = useState(false);
  const [startXMouse, setStartXMouse] = useState(0);
  const [startYMouse, setStartYMouse] = useState(0);
  const [currentXMouse, setCurrentXMouse] = useState(0);
  const [currentYMouse, setCurrentYMouse] = useState(0);

  const complexAdd = (c1, c2) => ({
    x: c1.x + c2.x,
    y: c1.y + c2.y,
  });

  // (x1 + y1i)(x2 + y2i) = [(x1*x2) + (x1*y2i) + (y1i*x2) + (y2i*y1i)]
  //  = [(x1*x2)  + i(x1*y2 + x2*y1)   - y2yl  ]
  const complexMultiply = (c1, c2) => ({
    x: c1.x * c2.x - c1.y * c2.y,
    y: c1.x * c2.y + c2.x * c1.y,
  });

  useEffect(() => {
    if (screenWidth <= 0 || screenHeight <= 0) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    context.clearRect(0, 0, canvas.width, canvas.height);
    const { width, height } = canvas.getBoundingClientRect();

    if (width <= 0 || height <= 0) return;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    const maxIterations = 100;
    const numberOfColors = 32;

    const randomColors = [...Array(numberOfColors)].map((_) => ({
      r: Math.floor(Math.random() * 255) + 1,
      g: Math.floor(Math.random() * 255) + 1,
      b: Math.floor(Math.random() * 255) + 1,
    }));

    const imageData = context.createImageData(width, height);
    const { data } = imageData;
    for (let y = 0; y <= height; y++) {
      for (let x = 0; x <= width; x++) {
        const n = y * width * 4 + x * 4;
        // Screen to complex cartesian conversion
        const c = {
          x: startX + (x / width) * (endX - startX),
          y: startY - (y / height) * (startY - endY),
        };
        let f = { x: 0, y: 0 };
        let isInSet = true;
        let i = 0;
        for (; i < maxIterations && isInSet; i++) {
          f = complexAdd(complexMultiply(f, f), c);
          isInSet = f.x >= -2 && f.x <= 2 && f.y >= -2 && f.y <= 2;
        }

        const { r, g, b } = isInSet
          ? { r: 0, g: 0, b: 0 }
          : randomColors[Math.floor(i % randomColors.length)];

        data[n] = r;
        data[n + 1] = g;
        data[n + 2] = b;
        data[n + 3] = 255;
      }
    }
    context.putImageData(imageData, 0, 0);
  }, [screenWidth, screenHeight, startX, startY, endX, endY]);

  const updateZoomCanvas = () => {
    const { current: canvas } = zoomRef;
    const context = canvas.getContext("2d");

    context.clearRect(0, 0, canvas.width, canvas.height);
    const { width, height } = canvas.getBoundingClientRect();

    if (width <= 0 || height <= 0) return;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    if (!isDragging) return;

    context.lineWidth = "1";
    context.strokeStyle = "red";

    const rect = {
      x: startXMouse,
      y: startYMouse,
      w: currentXMouse - startXMouse,
      h: currentYMouse - startYMouse,
    };

    context.beginPath();
    context.rect(rect.x, rect.y, rect.w, rect.h);
    context.stroke();
  };

  useEffect(() => {
    updateZoomCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startXMouse, startYMouse, currentXMouse, currentYMouse]);

  const getClientMouseXY = ({ target, clientX, clientY }) => {
    const rect = target.getBoundingClientRect();
    return {
      clientX: clientX - rect.left,
      clientY: clientY - rect.top,
    };
  };

  const onZoomMouseDown = (e) => {
    const { clientX, clientY } = getClientMouseXY(e);
    setIsDragging(true);
    setStartXMouse(clientX);
    setStartYMouse(clientY);
    setCurrentXMouse(clientX);
    setCurrentYMouse(clientY);

    console.log(clientX, clientY);
  };

  const onZoomMouseMove = (e) => {
    const { clientX, clientY } = getClientMouseXY(e);
    if (!isDragging) return;
    setCurrentXMouse(clientX);
    setCurrentYMouse(clientY);
  };

  const zoom = ({ x, y, w, h }) => {
    const canvas = zoomRef.current;
    const { width: screenWidth, height: screenHeight } =
      canvas.getBoundingClientRect();
    const coordsWidth = endX - startX;
    const coordsHeight = startY - endY;

    const newStartX = startX + x * (coordsWidth / screenWidth);
    const newEndX = newStartX + w * (coordsWidth / screenWidth);
    const newStartY = startY - y * (coordsHeight / screenHeight);
    const newEndY = newStartY - h * (coordsHeight / screenHeight);

    setStartX(newStartX);
    setEndX(newEndX);
    setStartY(newStartY);
    setEndY(newEndY);
  };

  const onZoomMouseUp = (e) => {
    const { clientX, clientY } = getClientMouseXY(e);
    setIsDragging(false);
    setCurrentXMouse(clientX);
    setCurrentYMouse(clientY);
    zoom(
      getAdjustedRect(startXMouse, startYMouse, currentXMouse, currentYMouse)
    );
  };

  const getAdjustedRect = (startX, startY, currentX, currentY) => ({
    x: Math.min(startX, startX + (currentX - startX)),
    y: Math.min(startY, startY + (currentX - startX)),
    w: Math.abs(currentX - startX),
    h: Math.abs(currentY - startY),
  });

  return (
    <div className="position-relative w-100" style={{ height: "800px" }}>
      <canvas
        className="position-absolute w-100 h-100"
        style={{
          minWidth: 0,
          top: 0,
          left: 0,
          backgroundColor: "grey",
          zIndex: 0,
        }}
        ref={canvasRef}
      />
      <canvas
        className="position-absolute w-100 h-100"
        style={{
          minWidth: 0,
          top: 0,
          left: 0,
          zIndex: 1,
        }}
        ref={zoomRef}
        onMouseDown={onZoomMouseDown}
        onMouseUp={onZoomMouseUp}
        onMouseMove={onZoomMouseMove}
      />
    </div>
  );
};

export default Index;
